import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import * as Bixby from '$/core';
import { GraphService } from './GraphService';
import { MsalService } from './MsalService';

export abstract class PlannerService extends GraphService {
  protected static route = '/planner/tasks';

  protected static async deleteTask(id: string | undefined): Promise<void> {
    if (Bixby.Utils.isNullOrEmpty(id)) {
      throw new Error('Invalid planner task ID.');
    }

    const token: string | null = await MsalService.getPlannerTokenRedirect();

    if (!token || token.length <= 0) {
      throw new Error('Token not found');
    }

    // Try to delete task
    const getTaskResponse: MicrosoftGraph.PlannerTask = await PlannerService.get<MicrosoftGraph.PlannerTask>(
      token,
      `${PlannerService.route}/${id}`
    );

    const deletePlannerTaskHeaders: Headers = new Headers();
    deletePlannerTaskHeaders.append('If-Match', (getTaskResponse as any)['@odata.etag']); // TODO - odata.etag TypeScript?
    await PlannerService.delete(token, `${PlannerService.route}/${getTaskResponse.id}`, deletePlannerTaskHeaders);
  }

  protected static createAssignees: (model: Bixby.Models.PlannerTask) => MicrosoftGraph.PlannerAssignments = (
    model: Bixby.Models.PlannerTask
  ) => {
    let orderHint = '';

    const result: any = {};

    (model.assignees || []).map(assignee => {
      result[assignee] = {
        '@odata.type': '#microsoft.graph.plannerAssignment',
        orderHint: (orderHint += ' !'),
      };
    });

    return result;
  };

  protected static initializeChecklist: (
    checklistItems: Bixby.Models.ChecklistItem[]
  ) => MicrosoftGraph.PlannerChecklistItems = (checklistItems: Bixby.Models.ChecklistItem[]) => {
    // Reverse the list to apply ordering
    const reversedChecklistItems: Bixby.Models.ChecklistItem[] = checklistItems.slice().reverse();

    const result: any = {};
    let orderHint = '';
    for (let i = 0; i < reversedChecklistItems.length; i++) {
      result[reversedChecklistItems[i].id] = {
        '@odata.type': 'microsoft.graph.plannerChecklistItem',
        isChecked: false,
        title: reversedChecklistItems[i].title,
        orderHint: (orderHint += ' !'),
      };
    }

    return result;
  };

  protected static sanitizeCheckListItemValue(item: string) {
    return (item || '').slice(0, 100);
  }

  protected static async updateTaskDetails(
    planId: string | undefined,
    token: string,
    taskDetailsEtag: string,
    checklist: MicrosoftGraph.PlannerChecklistItems,
    notes?: string
  ): Promise<void> {
    const updatePlannerTaskDetails = {
      checklist,
      ...(!Bixby.Utils.isNullOrEmpty(notes) && { description: notes }),
    };

    const updatePlannerTaskDetailsHeaders: Headers = new Headers();
    updatePlannerTaskDetailsHeaders.append('If-Match', taskDetailsEtag); // TODO - odata.etag TypeScript?
    updatePlannerTaskDetailsHeaders.append('Prefer', 'return=representation');

    await PlannerService.patch(
      token,
      `${PlannerService.route}/${planId}/details`,
      updatePlannerTaskDetails,
      updatePlannerTaskDetailsHeaders
    );
  }

  protected static async updateChecklistOrder(
    planId: string | undefined,
    token: string,
    count?: number
  ): Promise<void> {
    // Attempt to reorder at most 15 times
    const internalCount = count || 1;
    if (internalCount > 15) {
      throw new Error('Unable to update checklist order.');
    }

    // Get task details
    const updatedPlannerTaskDetailsResponse: MicrosoftGraph.PlannerTaskDetails =
      await PlannerService.get<MicrosoftGraph.PlannerTaskDetails>(token, `${PlannerService.route}/${planId}/details`);

    // Get new orderHint mapping
    const currentChecklistItems: Bixby.Models.ChecklistItem[] = [];
    for (const key in updatedPlannerTaskDetailsResponse.checklist) {
      const item = (updatedPlannerTaskDetailsResponse.checklist as any)[key] as MicrosoftGraph.PlannerChecklistItem;
      currentChecklistItems.push({
        id: key,
        title: item.title ?? '',
        orderHint: item.orderHint ?? '',
        internalSort: item.title ? parseInt(item.title.split(')')[0]) : -1,
      });
    }

    // Sort the checklist
    currentChecklistItems.sort((a, b) => {
      if (!a.orderHint || !b.orderHint) return 0;

      return a.orderHint < b.orderHint ? -1 : a.orderHint > b.orderHint ? 1 : 0;
    });

    // Reverse them to interpret what Planner is displaying
    currentChecklistItems.reverse();

    // Iterate the array, checking if there is a discrepancy in the sort
    for (let i = 0; i < currentChecklistItems.length; i++) {
      const currentItem = currentChecklistItems[i];
      const sortOrder = i + 1;

      if (sortOrder !== currentItem.internalSort) {
        // Sort in the correct order
        currentChecklistItems.sort((a, b) => {
          return (a.internalSort ?? 0) < (b.internalSort ?? 0)
            ? -1
            : (a.internalSort ?? 0) > (b.internalSort ?? 0)
            ? 1
            : 0;
        });

        // Attempt to save
        const initializedChecklist = PlannerService.initializeChecklist(currentChecklistItems);
        await PlannerService.updateTaskDetails(
          planId,
          token,
          (updatedPlannerTaskDetailsResponse as any)['@odata.etag'],
          initializedChecklist,
          updatedPlannerTaskDetailsResponse.description || ''
        );

        // Delay
        await new Promise(r => setTimeout(r, 1000));

        await PlannerService.updateChecklistOrder(planId, token, internalCount + 1);

        break;
      }
    }
  }
}
